import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { INK, OLIVE, SERIF, BODY } from './DagbladFrontPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfg = { withCredentials: true };

export function EditableLead({ ed, edition, custom, onSaved, onReset }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(ed);
  const open = () => { setDraft(ed); setEditing(true); };
  const save = async () => {
    try {
      const { data } = await axios.put(`${API}/dagblad/editorial`, { edition, ...draft }, cfg);
      onSaved(data); setEditing(false); toast.success('📰 Förstasidan uppdaterad');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kunde inte spara'); }
  };
  const field = 'w-full bg-transparent border-2 border-dashed p-2 outline-none';
  if (editing) {
    return (
      <div className="py-5 space-y-2" data-testid="dagblad-editorial-edit">
        <input value={draft.headline} onChange={(e) => setDraft({ ...draft, headline: e.target.value })} maxLength={200}
          className={`${field} text-3xl font-black`} style={{ fontFamily: SERIF, borderColor: OLIVE }} data-testid="dagblad-edit-headline" />
        <textarea value={draft.lead} onChange={(e) => setDraft({ ...draft, lead: e.target.value })} rows={4} maxLength={1200}
          className={`${field} text-base`} style={{ fontFamily: BODY, borderColor: OLIVE }} data-testid="dagblad-edit-lead" />
        <input value={draft.elsa_quote} onChange={(e) => setDraft({ ...draft, elsa_quote: e.target.value })} maxLength={300}
          className={`${field} italic`} style={{ fontFamily: SERIF, borderColor: OLIVE }} data-testid="dagblad-edit-quote" />
        <div className="flex gap-2">
          <button onClick={save} className="px-4 py-1.5 text-xs font-black uppercase tracking-widest text-[#F4EEE1]" style={{ background: INK }} data-testid="dagblad-edit-save">Spara</button>
          <button onClick={() => setEditing(false)} className="px-4 py-1.5 text-xs font-black uppercase tracking-widest border-2" style={{ borderColor: INK }} data-testid="dagblad-edit-cancel">Avbryt</button>
        </div>
      </div>
    );
  }
  return (
    <div className="relative group py-5" data-testid="dagblad-editorial">
      <button onClick={open} className="text-left w-full" title="Klicka för att ändra" data-testid="dagblad-edit-open">
        <h2 className="text-4xl sm:text-5xl font-black leading-[1.05] group-hover:underline decoration-dashed decoration-2 underline-offset-8" style={{ fontFamily: SERIF, color: INK }} data-testid="dagblad-headline">{ed.headline}</h2>
        <p className="mt-3 text-base sm:text-lg leading-relaxed first-letter:text-5xl first-letter:font-black first-letter:float-left first-letter:mr-2 first-letter:leading-none"
          style={{ fontFamily: BODY, color: INK }} data-testid="dagblad-lead">{ed.lead}</p>
        <blockquote className="mt-4 pl-4 border-l-4 italic text-base" style={{ borderColor: OLIVE, fontFamily: SERIF, color: OLIVE }} data-testid="dagblad-quote">
          ”{ed.elsa_quote}” <span className="not-italic text-xs font-bold uppercase tracking-widest">— {custom ? 'du och Farmor Elsa' : 'Farmor Elsa'}</span>
        </blockquote>
      </button>
      <div className="no-print mt-2 flex gap-3 text-[11px] font-black uppercase tracking-widest" style={{ color: OLIVE }}>
        <button onClick={open} className="underline underline-offset-4" data-testid="dagblad-edit-open-btn">✏️ Ändra rubrik och text</button>
        {custom && <button onClick={onReset} className="underline underline-offset-4" data-testid="dagblad-edit-reset">↺ Låt Elsa skriva igen</button>}
      </div>
    </div>
  );
}

export function InlineAdd({ placeholder, withTime, onAdd, testid }) {
  const [text, setText] = useState('');
  const [time, setTime] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    try { await onAdd(text.trim(), time); setText(''); setTime(''); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="no-print mt-2 flex flex-wrap items-center gap-2" data-testid={testid}>
      {withTime && <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="bg-transparent border-b-2 px-1 py-1 text-sm outline-none w-[88px]" style={{ borderColor: INK }} data-testid={`${testid}-time`} />}
      <input value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} maxLength={140}
        className="flex-1 min-w-[140px] bg-transparent border-b-2 px-1 py-1 text-sm outline-none" style={{ borderColor: INK, fontFamily: BODY }} data-testid={`${testid}-input`} />
      <button type="submit" disabled={busy} className="px-2.5 py-1 text-xs font-black text-[#F4EEE1] disabled:opacity-50" style={{ background: OLIVE }} data-testid={`${testid}-add`}>+</button>
    </form>
  );
}

export function MissingSections({ p, onShow }) {
  const missing = p.all_sections.filter((x) => !p.sections.includes(x.id));
  if (!missing.length) return null;
  return (
    <div className="no-print mt-6 p-3 border-2 border-dashed text-center" style={{ borderColor: OLIVE }} data-testid="dagblad-missing">
      <p className="text-xs font-black uppercase tracking-[0.25em] mb-2" style={{ color: OLIVE }}>Saknar du något? Lägg till i tidningen</p>
      <div className="flex flex-wrap justify-center gap-1.5">
        {missing.map((x) => (
          <button key={x.id} onClick={() => onShow(x.id)} className="px-2.5 py-1 border-2 text-xs font-bold transition-transform hover:scale-105" style={{ borderColor: INK }} data-testid={`dagblad-show-${x.id}`}>
            + {x.emoji} {x.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export async function addTodo(text) {
  await axios.post(`${API}/daily-flow/items`, { text }, cfg);
  toast.success('✅ Tillagd i Idag-listan');
}

export async function addEvent(day, title, time) {
  const starts = time ? new Date(`${day}T${time}`) : new Date(`${day}T00:00`);
  await axios.post(`${API}/calendar/events`, { title, starts_at: time ? starts.toISOString() : `${day}T00:00:00`, all_day: !time, category: 'other', in_paper: true, auto_reminders: true }, cfg);
  toast.success('📅 Händelsen ligger nu i kalendern');
}
